import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Plus, X, MessageSquarePlus, Phone, MapPin, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api";
import { useAuth } from "@/auth";
import { OFFICE, OfficeCard, OfficeButton, OfficeField, OfficeInput, OfficeTextarea } from "@/pages/admin/officeUi";
import { TIPOLOGIE, esitoLabel, tipologiaLabel, fmtDate, fmtEur, CompensoPill } from "@/pages/partner/labels";

const EMPTY = { owner_name: "", phone: "", email: "", structure_name: "", comune: "", tipologia: "bnb", note: "", consent: false };

function Stat({ label, value, testId, accent }) {
  return (
    <div className="rounded-2xl border p-4" style={{ background: OFFICE.surface, borderColor: OFFICE.border }}>
      <div className="text-xs uppercase tracking-wide" style={{ color: OFFICE.muted }}>{label}</div>
      <div data-testid={testId} className="font-display text-2xl mt-1" style={{ color: accent ? OFFICE.brand : OFFICE.ink }}>{value}</div>
    </div>
  );
}

function NewReferralForm({ onClose, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.consent) { toast.error("Serve il consenso del proprietario a essere contattato"); return; }
    setBusy(true);
    try {
      const body = { ...f, email: f.email.trim() || null };
      const r = await api.partnerCreateReferral(body);
      if (r.status === "duplicate") toast.warning(r.duplicate_message, { duration: 8000 });
      else toast.success("Segnalazione inviata, grazie!");
      onSaved();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40" data-testid="new-referral-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <form onSubmit={submit} className="w-full sm:max-w-lg max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl px-6 pb-6 space-y-4"
        style={{ background: OFFICE.surface }}>
        <div className="sticky top-0 z-10 -mx-6 px-6 py-4 flex items-center justify-between border-b" style={{ background: OFFICE.surface, borderColor: OFFICE.border }}>
          <h2 className="font-display text-lg">Nuova segnalazione</h2>
          <button type="button" onClick={onClose} data-testid="new-referral-close" aria-label="Chiudi"
            className="h-10 w-10 rounded-full flex items-center justify-center transition-colors hover:bg-[#F5F3EF]" style={{ background: OFFICE.surface2 }}>
            <X className="h-5 w-5" />
          </button>
        </div>
        <OfficeField label="Nome e cognome proprietario *"><OfficeInput required value={f.owner_name} onChange={set("owner_name")} data-testid="ref-owner-name" /></OfficeField>
        <OfficeField label="Telefono *"><OfficeInput required type="tel" inputMode="tel" value={f.phone} onChange={set("phone")} placeholder="333 1234567" data-testid="ref-phone" /></OfficeField>
        <OfficeField label="Email (facoltativa)"><OfficeInput type="email" value={f.email} onChange={set("email")} data-testid="ref-email" /></OfficeField>
        <OfficeField label="Nome della struttura *"><OfficeInput required value={f.structure_name} onChange={set("structure_name")} data-testid="ref-structure-name" /></OfficeField>
        <OfficeField label="Comune *"><OfficeInput required value={f.comune} onChange={set("comune")} data-testid="ref-comune" /></OfficeField>
        <OfficeField label="Tipologia">
          <select value={f.tipologia} onChange={set("tipologia")} data-testid="ref-tipologia"
            className="w-full rounded-xl border px-4 py-2.5" style={{ borderColor: OFFICE.border, background: OFFICE.surface }}>
            {TIPOLOGIE.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </OfficeField>
        <OfficeField label="Note"><OfficeTextarea rows={3} value={f.note} onChange={set("note")} placeholder="Es. scontento del suo property manager" data-testid="ref-note" /></OfficeField>
        <label className="flex items-start gap-3 text-sm rounded-xl p-3" style={{ background: OFFICE.brandTertiary }}>
          <input type="checkbox" checked={f.consent} onChange={set("consent")} className="mt-0.5 h-5 w-5 accent-[#D9501F]" data-testid="ref-consent" />
          <span>Il proprietario ha dato il consenso a essere contattato da YU YieldUp *</span>
        </label>
        <div className="flex flex-col-reverse sm:flex-row gap-2">
          <OfficeButton type="button" variant="ghost" onClick={onClose} className="w-full" data-testid="ref-cancel">Annulla</OfficeButton>
          <OfficeButton type="submit" disabled={busy} className="w-full" data-testid="ref-submit">{busy ? "Invio…" : "Invia segnalazione"}</OfficeButton>
        </div>
      </form>
    </div>
  );
}

function NoteBox({ rid, onSaved }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { await api.partnerAddNote(rid, text.trim()); setText(""); toast.success("Nota aggiunta"); onSaved(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="flex gap-2 mt-3">
      <OfficeInput value={text} onChange={(e) => setText(e.target.value)} placeholder="Aggiungi una nota…" data-testid={`ref-note-input-${rid}`} />
      <OfficeButton variant="ghost" onClick={save} disabled={busy} data-testid={`ref-note-save-${rid}`}><MessageSquarePlus className="h-4 w-4" /></OfficeButton>
    </div>
  );
}

function ReferralCard({ r, onChanged }) {
  return (
    <OfficeCard className="!p-4" data-testid={`partner-referral-${r.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-medium truncate">{r.structure_name}</div>
          <div className="text-sm truncate" style={{ color: OFFICE.muted }}>{r.owner_name} · {tipologiaLabel(r.tipologia)}</div>
        </div>
        <CompensoPill status={r.compenso.status} testId={`ref-compenso-status-${r.id}`} />
      </div>
      {r.status === "duplicate" ? (
        <div className="text-sm mt-3 rounded-lg p-2.5" style={{ background: "#EDEAE5" }} data-testid={`ref-duplicate-msg-${r.id}`}>{r.duplicate_message}</div>
      ) : null}
      <div className="grid grid-cols-2 gap-x-3 gap-y-2 mt-3 text-sm">
        <span className="flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" style={{ color: OFFICE.muted }} />{fmtDate(r.created_at)}</span>
        <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" style={{ color: OFFICE.muted }} />{r.comune}</span>
        <span className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" style={{ color: OFFICE.muted }} />{r.contacted ? "Contattato" : "Non ancora contattato"}</span>
        <span data-testid={`ref-esito-${r.id}`}>Esito: <b>{esitoLabel(r.esito)}</b></span>
        {r.status !== "duplicate" ? (
          <>
            <span>Compenso: <b data-testid={`ref-compenso-amount-${r.id}`}>{fmtEur(r.compenso.amount)}</b></span>
            <span>Scadenza: {fmtDate(r.compenso.deadline)}</span>
          </>
        ) : null}
      </div>
      {r.note ? <p className="text-sm mt-3" style={{ color: OFFICE.ink2 }}>“{r.note}”</p> : null}
      {r.partner_notes?.map((n, i) => (
        <p key={i} className="text-xs mt-2 pl-3 border-l-2" style={{ borderColor: OFFICE.brand, color: OFFICE.ink2 }}>{fmtDate(n.created_at)} · {n.text}</p>
      ))}
      <NoteBox rid={r.id} onSaved={onChanged} />
    </OfficeCard>
  );
}

export default function PartnerHome() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [dash, setDash] = useState(null);
  const [refs, setRefs] = useState([]);
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(async () => {
    try {
      const [d, r] = await Promise.all([api.partnerDashboard(), api.partnerReferrals()]);
      setDash(d); setRefs(r);
    } catch (e) {
      if (e.status === 403) {
        toast.error(e.message, { id: "partner-disabled" });
        logout(); navigate("/login", { replace: true });
      } else toast.error(e.message);
    }
  }, [logout, navigate]);
  useEffect(() => { load(); }, [load]);

  const t = dash?.totals || {};
  return (
    <div className="theme-office min-h-screen pb-28" style={{ background: OFFICE.surface2, color: OFFICE.ink }} data-testid="partner-home">
      <header className="sticky top-0 z-30" style={{ background: OFFICE.surface, borderBottom: `1px solid ${OFFICE.border}` }}>
        <div className="max-w-4xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="font-display text-lg">YU <span style={{ color: OFFICE.brand }}>YieldUp</span> <span className="text-sm font-sans font-normal" style={{ color: OFFICE.muted }}>· Partner</span></div>
          <OfficeButton variant="ghost" onClick={() => { logout(); navigate("/login", { replace: true }); }} data-testid="partner-logout"><LogOut className="h-4 w-4" /></OfficeButton>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-5 py-6">
        <h1 className="font-display text-2xl sm:text-3xl" data-testid="partner-agency-name">{dash?.partner?.agency_name || "…"}</h1>
        <p className="text-sm mt-1" style={{ color: OFFICE.muted }}>Ogni segnalazione resta tua per 12 mesi. Se diventa cliente YU Follow ricevi una mensilità di canone.</p>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-6">
          <Stat label="Inviate" value={t.sent ?? "—"} testId="stat-sent" />
          <Stat label="Contattate" value={t.contacted ?? "—"} testId="stat-contacted" />
          <Stat label="Clienti" value={t.clients ?? "—"} testId="stat-clients" />
          <Stat label="Maturati" value={fmtEur(t.accrued)} testId="stat-accrued" accent />
          <Stat label="Pagati" value={fmtEur(t.paid)} testId="stat-paid" />
        </div>
        <div className="flex items-center justify-between mt-8 mb-3">
          <h2 className="font-display text-lg">Le tue segnalazioni</h2>
          <OfficeButton onClick={() => setShowNew(true)} className="hidden sm:inline-flex" data-testid="new-referral-btn"><Plus className="h-4 w-4" /> Nuova segnalazione</OfficeButton>
        </div>
        <div className="space-y-3" data-testid="partner-referral-list">
          {refs.length === 0 ? <OfficeCard className="text-sm text-center" style={{ color: OFFICE.muted }}>Nessuna segnalazione ancora. Inizia con “Nuova segnalazione”.</OfficeCard> : null}
          {refs.map((r) => <ReferralCard key={r.id} r={r} onChanged={load} />)}
        </div>
      </main>
      <div className="sm:hidden fixed bottom-0 inset-x-0 p-4" style={{ background: "linear-gradient(transparent, #F5F3EF 40%)" }}>
        <OfficeButton onClick={() => setShowNew(true)} className="w-full py-3.5" data-testid="new-referral-btn-mobile"><Plus className="h-4 w-4" /> Nuova segnalazione</OfficeButton>
      </div>
      {showNew ? <NewReferralForm onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} /> : null}
    </div>
  );
}
